import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { Button } from '../components/Button'
import { isMetaVariant } from '../lib/variant'

interface Testimonial {
  name: string
  stage?: string
  text: string
  /** Store star rating. Leave unset for an email: no stars shown. */
  rating?: number
  /** When true, this testimonial includes an outcome/efficacy claim. Hidden on
   *  meta variants because ads in Special Ad Category can't be optimized for
   *  campaigns whose destination pages make medical-outcome claims. */
  outcomeClaim?: boolean
}

// Real customer words, quoted word for word (… marks a cut). Store reviews carry their real
// star rating; emails we were sent have no rating, so they show no stars. No quote claims a
// lab, stage or "saved my life" result: a testimonial's result counts as our own claim.
// Traced 2026-10-21 (App Store Connect API, Play Console exports, the boss man's saved-feedback list).
const stories: Testimonial[] = [
  {
    name: 'Joyce', // email
    text: "This app is EVERYTHING! I have shown it to my nephrologists and they are showing it to their dietitians. You have taken out the stress of figuring out each meal and helped break down my meals. I am no longer fighting with my children over what I'm allowed and what I'm not allowed 😂",
  },
  {
    name: 'Lola', // email
    text: "I'm so relieved I found this app. I have stage 4 kidney disease and would have been in despair without it. I'm no longer afraid to eat, and I've learned so much from the app that \"What can I eat?\" no longer weighs on me. … Thank you for this app. It means so much to me ❤️",
  },
  {
    name: 'JBGRAL', // App Store US, 2026-01-26
    text: "I have searched all over for the perfect app and this is it. I showed it to my nephrologist and he loved it so much. He's showing it to all his Dietitians. This app takes the thinking out of what you're cooking and how much to eat. It does the thinking for you and I highly recommend it.",
    rating: 5,
  },
  {
    name: 'ChelB', // App Store CA, 2026-08-31
    text: "This app has been immensely helpful in tracking my sodium, potassium, phosphorus, protein, fluid, and calorie intake for kidney disease. It is continuously being updated with very helpful features and just keeps getting better!",
    rating: 5,
  },
  {
    name: 'Cynthia', // email
    text: "So far, I'm really enjoying this app, it's been a huge help in managing my potassium, phosphorus, and fluids, which have been out of range for a long time. The swap ideas are fantastic. I've learned so much and I'm hopeful they'll help me build better long-term habits.",
  },
  {
    name: 'Scott West', // Google Play, 2026-09-12
    text: "This app is easily worth every penny paid. It simplifies the management of my CKD. The AI features make it easy to log my food intake, however I can still dive in and get really granular if I desire.",
    rating: 5,
  },
  {
    name: 'Anita', // email
    text: "…my nephrologist and renal dietitian liked the pdf summary of the nutrients I have consumed since I began Kidney Pal. My nephrologist didn't seem to be aware of Kidney Pal and said she has other patients who could benefit. Bravo to you!",
  },
  {
    name: 'JR', // Google Play, 2026-08-11
    stage: '3b',
    text: "As a CKD Stage 3b patient, this app makes tracking a compliant CKD diet easy! With the barcode scanner, I can easily look up the nutrients of foods at the grocery, with the photo upload, I have the same great information available, all customizable if I need to alter a serving size. … This is a game changer for CKD patients!",
    rating: 5,
  },
  {
    name: 'Pat', // Google Play, 2026-07-16
    text: "All I have to do is speak into the microphone and tell the app what I consumed. The app then calculates my nutritional information. It's easy to navigate and provides a great way to educate myself on trends and surprises!",
    rating: 5,
  },
  {
    name: 'Lillyannlou', // App Store, 2026-05-31
    text: "I'm 43 and have stage 4 CKD. This app is really helping me managing my diet. It's very user friendly. Calculates daily Electrolytes and Phosphate intake. Great recipes.",
    rating: 5,
  },
]

export function SocialProofStep() {
  const { goNext } = useOnboardingStore()
  const [currentStory, setCurrentStory] = useState(0)
  const isMeta = isMetaVariant()

  // On meta variants, filter out testimonials with outcome/efficacy claims to
  // keep ad-compliance margin. Organic visitors see the full rotation.
  const filteredStories = isMeta
    ? stories.filter((s) => !s.outcomeClaim)
    : stories

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStory((prev) => (prev + 1) % filteredStories.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [filteredStories.length])

  const story = filteredStories[currentStory]

  return (
    <div className="min-h-dvh flex flex-col bg-white">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <p className="text-sm text-primary font-semibold tracking-wider mb-2">TRUSTED BY THOUSANDS</p>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              What people say about KidneyPal
            </h1>
            <p className="text-gray-500">
              Join 40,000+ people who have used KidneyPal to manage their kidney diet
            </p>
          </div>

          <motion.div
            key={currentStory}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-teal-50 rounded-2xl p-6 mb-6"
          >
            {story.rating && (
              <div className="flex gap-0.5 mb-3">
                {[...Array(story.rating)].map((_, i) => (
                  <span key={i} className="text-amber-400 text-lg">★</span>
                ))}
              </div>
            )}
            <p className="text-gray-700 mb-4 italic">"{story.text}"</p>
            <div>
              <p className="font-semibold text-gray-900">{story.name}</p>
              {story.stage && <p className="text-sm text-primary">{story.stage}</p>}
            </div>
          </motion.div>

          <div className="flex justify-center gap-2 mb-8 flex-wrap">
            {filteredStories.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStory(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  i === currentStory ? 'bg-primary' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 font-semibold px-4 py-2 rounded-full">
              <span>✓</span>
              <span>Used by 40,000+ people</span>
            </div>
          </div>

          <Button onClick={() => goNext()}>Let's personalize for you</Button>
        </motion.div>
      </div>
    </div>
  )
}
